import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { ReleaseLinkData } from '../releaseLink/types';

/**
 * What both resolvers report while they turn one thing — a catalog row or a
 * pasted url — into the set of music service links a release page is made of.
 *
 * The three service lists are what makes resolution watchable. A service moves
 * from `pending` to `found` or `missing` as the answers arrive, so the sheet
 * can show progress instead of a spinner that says nothing.
 */
export interface ReleaseResolution {
  /** Still reading the release or still searching the other services. */
  isResolving: boolean;
  error?: unknown;
  retry: () => void;
  /** The link the release page is built around. */
  main?: ReleaseLinkData;
  /** Every other music service link found so far. */
  others: ReleaseLinkData[];
  foundSources: MusicAPIIntegrationID[];
  pendingSources: MusicAPIIntegrationID[];
  missingSources: MusicAPIIntegrationID[];
  /** `YYYY-MM-DD`, only when the source knows the day. */
  releaseDate?: string;
  title?: string;
  artistName?: string;
  albumName?: string;
  imageUrl?: string;
  /** The ISRC of a pasted track link, when the service gives it. */
  isrc?: string;
  /** Where the release was read from, for the line under the title. */
  sourceLabel?: string;
  /** Resolution finished and found no music service link at all. */
  hasNoLinks: boolean;
}

/** Nothing is being resolved, so there is nothing to retry either. */
function noRetry(): void {
  // Deliberately nothing: this stands in where no resolver is running.
}

export const emptyReleaseResolution: ReleaseResolution = {
  isResolving: false,
  retry: noRetry,
  others: [],
  foundSources: [],
  pendingSources: [],
  missingSources: [],
  hasNoLinks: false,
};
